import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { of } from 'rxjs';

import { CURRENCY_NAMES, TimeService } from '@core/common-legacy';
import { AttachmentsListComponent } from '@core/components-attachments-list-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { I18nService } from '@core/i18n-legacy';
import type {
  InterviewResultAnswer,
  InterviewResultAnswerOption,
  RatingLevelTranslations,
} from '@core/interview-results-types';
import { InterviewEvaluationRatingComponent } from '@core/interview-results-ui';
import { QuestionKind } from '@core/interview-templates-types';
import { provideFilteredMentions } from '@core/notes-legacy';
import { RenderDirective, WysiwygContext$ } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorDefaultConfig,
  ProsemirrorDefaultConfigDirective,
} from '@core/prosemirror-legacy/webapp';
import {
  DividerComponent,
  BoxComponent,
  IconComponent,
  HeadingComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-interview-answer-list',
  templateUrl: 'interview-answer-list.component.html',
  styleUrls: ['./interview-answer-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeadingComponent,
    IconComponent,
    BoxComponent,
    InterviewEvaluationRatingComponent,
    DividerComponent,
    AttachmentsListComponent,
    RtTooltip,
    RenderDirective,
    ProsemirrorDefaultConfigDirective,
  ],
  providers: [
    { provide: WysiwygContext$, useFactory: () => new WysiwygContext$(null) },
    ...provideFilteredMentions(),
  ],
})
export class InterviewAnswerListComponent {
  private readonly i18n = inject(I18nService);
  private readonly timeService = inject(TimeService);
  private readonly currencies = toSignal(
    inject(CURRENCY_NAMES, { optional: true }) ?? of<Record<string, string>>({}),
    { initialValue: <Record<string, string>>{} },
  );

  protected readonly QuestionKind = QuestionKind;
  protected readonly ProsemirrorConfig = ProsemirrorDefaultConfig;
  protected readonly useProsemirror = injectDevFlag(DevFlag.ProsemirrorInEvaluations);

  public readonly answers = input<InterviewResultAnswer[]>(undefined!);
  public readonly canManageCandidates = input<boolean>(false);
  public readonly ratingLevelTranslations = input<RatingLevelTranslations>();

  protected translations = {
    attachment: this.i18n.translate('app.print.candidate_profile.attachment'),
    skillsHeading: this.i18n.translate('app.interview.summary.skills_heading'),
  };

  private BooleanAnswerTranslationsMap: Record<string, string> = {
    yes: this.i18n.translate('app.common.boolean.yes'),
    no: this.i18n.translate('app.common.boolean.no'),
  };

  public isFile = (kind: QuestionKind) => QuestionKind.File === kind;

  public isSimple = (kind: QuestionKind) =>
    [
      QuestionKind.Dropdown,
      QuestionKind.SingleChoice,
      QuestionKind.String,
      QuestionKind.Text,
    ].includes(kind);

  public isMultiChoice = (kind: QuestionKind) => QuestionKind.MultipleChoice === kind;

  public isBoolean = (kind: QuestionKind) => QuestionKind.Boolean === kind;

  public isDate = (kind: QuestionKind) => QuestionKind.Date === kind;

  public isSalary = (kind: QuestionKind) => QuestionKind.Salary === kind;

  public isSection = (kind: QuestionKind) => QuestionKind.Section === kind;

  public getFormattedDate = (content: string | undefined) =>
    content ? this.timeService.format({ input: content, pattern: 'DD MMM YYYY' }) : '';

  public getSalary(answer: InterviewResultAnswer): string {
    const amount = answer.content ?? '';
    const code = answer.questionOptions?.currency;
    return code ? `${amount} ${this.currencyName(code)}` : amount;
  }

  public hasAnswers(answer: InterviewResultAnswer): boolean {
    if (!answer) return false;

    return (
      !!(answer.content || '').length ||
      !!(answer.attachments || []).length ||
      !!(answer.skillRatings || []).length ||
      this.hasAnswer(answer.contents || [])
    );
  }

  public isScorecard = (kind: QuestionKind) => QuestionKind.Scorecard === kind;

  public isSkills = (kind: QuestionKind) => QuestionKind.Skills === kind;

  public getEvaluatedSkills(answer: InterviewResultAnswer) {
    const skills = answer.questionOptions?.skills || [];
    const ratings = answer.skillRatings || [];
    return skills.filter(skill =>
      ratings.some(r => r.skillId === skill.id && r.proficiencyId !== null),
    );
  }

  public getBooleanAnswer = (answer: string) => this.BooleanAnswerTranslationsMap[answer];

  public filterEmptyOptions(answer: InterviewResultAnswer): InterviewResultAnswerOption[] {
    return (answer.options || []).filter((option, index) => answer.contents![index]);
  }

  public filterEmptyContents(contents: string[]): string[] {
    return contents.filter(c => c && c !== '');
  }

  public trackByAnswer(index: number, answer: InterviewResultAnswer): number {
    return answer.id;
  }

  public trackByContent(index: number, content: string): string {
    return content + index;
  }

  public trackByOption(index: number, option: InterviewResultAnswerOption): string {
    return option.title;
  }

  private hasAnswer(contents: string[]): boolean {
    return !!contents.filter(c => c && c !== '').length;
  }

  private currencyName(code: string): string {
    const name = this.currencies()[code];
    return name ? `${name} (${code})` : code;
  }
}
